import { Link } from '@tanstack/react-router';
import { CalendarDays, Wallet } from 'lucide-react';
import type { BookingResponse } from '@/shared/api';
import { formatMoney, formatDate } from '@/shared/lib/format';
import { formatBookingPrice, isPriceUnderReview } from '@/shared/lib/bookingPrice';
import { formatOccupancy } from '@/shared/lib/occupancy';
import { PanelCard, FieldGrid, Field } from './PanelCard';
import { ProvenanceTag } from './ProvenanceTag';

function nights(checkIn: string, checkOut: string): number {
  const a = new Date(checkIn).getTime();
  const b = new Date(checkOut).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

function arrival(hour: string | null | undefined): string | null {
  if (!hour) return null;
  const [h, m] = hour.split(':');
  return h && m ? `${h}:${m}` : null;
}

export function StayContextCard({
  booking,
  propertyName,
}: {
  booking: BookingResponse;
  propertyName: string | null;
}) {
  const arr = arrival(booking.arrivalHour);
  return (
    <PanelCard
      icon={CalendarDays}
      title="Aufenthalt"
      defaultOpen
      trailing={<ProvenanceTag source="ota" />}
    >
      <FieldGrid>
        <Field
          label="Anreise"
          value={
            <>
              {formatDate(booking.checkInDate)}
              {arr && <span className="font-mono"> · {arr}</span>}
            </>
          }
        />
        <Field label="Abreise" value={formatDate(booking.checkOutDate)} />
        <Field label="Nächte" mono value={nights(booking.checkInDate, booking.checkOutDate)} />
        <Field label="Gäste" value={formatOccupancy(booking)} />
        {propertyName && <Field wide label="Apartment" value={propertyName} />}
      </FieldGrid>
      <Link
        to="/bookings/$id"
        params={{ id: booking.id }}
        className="rounded-pill border-ink text-ink hover:bg-card-alt mt-3 flex min-h-11 items-center justify-center border-[1.5px] text-[13px] font-bold"
      >
        Buchung öffnen
      </Link>
    </PanelCard>
  );
}

export function BillingContextCard({ booking }: { booking: BookingResponse }) {
  const c = booking.currency;
  const knownPrice = !isPriceUnderReview(booking);
  return (
    <PanelCard
      icon={Wallet}
      title="Abrechnung"
      hint={booking.source === 'Ota' ? 'Kanalbuchung' : 'Manuelle Buchung'}
      trailing={<ProvenanceTag source={booking.source === 'Ota' ? 'ota' : 'internal'} />}
    >
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-ink-faint text-[12px]">Gesamt</span>
        <span className="text-ink font-serif text-2xl">{formatBookingPrice(booking)}</span>
      </div>
      {knownPrice && booking.baseRate != null && (
        <Row label="Nachtrate laut Buchung" value={formatMoney(booking.baseRate, c)} />
      )}
      {knownPrice && booking.cleaningFee != null && booking.cleaningFee > 0 && (
        <Row label="Reinigungsgebühr" value={formatMoney(booking.cleaningFee, c)} />
      )}
      <div className="mt-2.5 flex items-center justify-between">
        <span className="text-sage text-[10px] font-bold uppercase tracking-[0.05em]">
          Zahlungsstatus aus diesen Buchungsdaten nicht belegt
        </span>
      </div>
    </PanelCard>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-line/60 flex items-baseline justify-between border-b py-1.5 last:border-b-0">
      <span className="text-ink-soft text-[12.5px]">{label}</span>
      <span className="text-ink font-mono text-[13px] tabular-nums">{value}</span>
    </div>
  );
}
